import { useEffect, useState, useCallback, useRef } from 'react';
import { useAIAssistantStore, ResponseFormatType, QueryClassification, OrchestrationDetails } from '../stores/aiAssistantStore';
import { useAppStore } from '../stores/appStore';
import { useAIAssistant, aiAssistantAPI } from '../hooks/useAIAssistant';
import { api } from '../lib/api';
import ChatHistorySidebar from '../components/ai-assistant/ChatHistorySidebar';
import MessageList from '../components/ai-assistant/MessageList';
import MessageInput from '../components/ai-assistant/MessageInput';
import Button from '../components/ui/Button';
import { AlertCircle, Menu, Zap, Briefcase, X } from 'lucide-react';
import { toast } from 'sonner';

interface ChatHistoryResponse {
  sessions: Array<{
    sessionId: string;
    userId: string;
    title: string;
    createdAt: string;
    updatedAt: string;
    messageCount: number;
    lastMessage?: string;
    messages?: any[];
  }>;
  total: number;
}

/**
 * AI Assistant Page
 * ChatGPT/Claude-style interface with auto-collapse navigation
 */
export default function AIAssistantPage() {
  const currentUser = useAppStore((state) => state.currentUser);
  const setSidebarCollapsed = useAppStore((state) => state.setSidebarCollapsed);
  const sidebarCollapsed = useAppStore((state) => state.sidebarCollapsed);
  const activeCase = useAppStore((state) => state.activeCase);

  // Build project context from active case
  const projectContext = activeCase ? {
    caseId: activeCase.id,
    projectName: activeCase.name,
    companyName: activeCase.companyName,
    businessDomain: activeCase.businessDomain?.description,
    currentPhase: activeCase.currentPhase,
  } : undefined;

  const {
    sessions,
    currentSessionId,
    messages,
    isLoading,
    isStreaming,
    streamingMessageId,
    streamingContent,
    error,
    createNewSession,
    setCurrentSession,
    addMessage,
    updateMessage,
    clearMessages,
    setLoading,
    setError,
    startStreaming,
    appendStreamContent,
    finishStreaming,
  } = useAIAssistantStore();

  const { sendQuery, useChatHistory, useHealthCheck } = useAIAssistant();

  const [wasCollapsed, setWasCollapsed] = useState<boolean | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [useStreamingMode, setUseStreamingMode] = useState<boolean>(true); // Toggle for streaming
  const [streamingStatus, setStreamingStatus] = useState<{ step: number; message: string; icon: string } | null>(null);

  // Ref to store classification data during streaming (doesn't need re-render)
  const classificationRef = useRef<QueryClassification | null>(null);

  // Fetch chat history
  const { data: historyData } = useChatHistory(currentUser?.id);

  // Health check
  const { data: healthData, isError: healthError } = useHealthCheck();

  // Auto-collapse primary navigation when AI Assistant is active
  useEffect(() => {
    // Store previous state when mounting
    if (wasCollapsed === null) {
      setWasCollapsed(sidebarCollapsed);
    }

    // Collapse sidebar if it's not already collapsed
    if (!sidebarCollapsed) {
      setSidebarCollapsed(true);
    }

    // Restore previous state when unmounting
    return () => {
      if (wasCollapsed !== null && wasCollapsed !== sidebarCollapsed) {
        setSidebarCollapsed(wasCollapsed);
      }
    };
  }, []); // Only run on mount/unmount

  // Show health error notification
  useEffect(() => {
    if (healthError) {
      toast.error('AI Assistant service is unavailable. Please try again later.');
    }
  }, [healthError]);

  // Handle new chat
  const handleNewChat = () => {
    if (!currentUser?.id) {
      toast.error('Please log in to start a new chat');
      return;
    }

    createNewSession(currentUser.id);
  };

  // Handle session selection
  const handleSessionSelect = async (sessionId: string) => {
    setCurrentSession(sessionId);

    // Find the session and load its messages
    const session = sessions.find((s) => s.sessionId === sessionId);
    if (session?.messages && session.messages.length > 0) {
      // If session already has messages loaded, use them
      clearMessages();
      session.messages.forEach((msg) => addMessage(msg));
    } else {
      // Load messages from backend using the dedicated endpoint
      try {
        const response = await api.get<{ sessionId: string; messages: any[]; total: number }>(
          `/ai-assistant/sessions/${sessionId}/messages`
        );

        if (response.messages && response.messages.length > 0) {
          // Clear current messages and load session messages
          clearMessages();
          response.messages.forEach((msg: any) => addMessage(msg));
        }
      } catch (error) {
        console.error('Failed to load session messages:', error);
        toast.error('Failed to load conversation history');
      }
    }
  };

  // Handle send message with streaming
  const handleSendMessageWithStreaming = useCallback(async (message: string) => {
    if (!currentUser?.id) {
      toast.error('Please log in to send messages');
      return;
    }

    const tempMessageId = `temp_${Date.now()}`;
    const timestamp = new Date().toISOString();

    // Add user message
    addMessage({
      messageId: `user_${Date.now()}`,
      sessionId: currentSessionId || '',
      role: 'user',
      content: message,
      timestamp,
      createdAt: timestamp,
    });

    // Add placeholder assistant message for streaming
    addMessage({
      messageId: tempMessageId,
      sessionId: currentSessionId || '',
      role: 'assistant',
      content: '',
      timestamp,
      createdAt: timestamp,
    });

    startStreaming(tempMessageId);
    setInputValue('');
    setStreamingStatus(null); // Clear any previous status
    classificationRef.current = null; // Clear any previous classification

    try {
      let sessionId = currentSessionId;
      let finalMessageId = tempMessageId;

      await aiAssistantAPI.streamQuery(
        {
          query: message,
          userId: currentUser.id,
          sessionId: currentSessionId || undefined,
          projectContext,
        },
        {
          onStart: (data) => {
            sessionId = data.sessionId;
            finalMessageId = data.messageId;
            if (!currentSessionId) {
              setCurrentSession(data.sessionId);
            }
          },
          onStatus: (data) => {
            setStreamingStatus(data);
          },
          onClassification: (data) => {
            // Store classification data for including in final message metadata
            classificationRef.current = {
              intent: data.intent,
              contexts: data.contexts || [],
              entities: {},
              dataSourcesRecommended: [],
              complexity: data.complexity || 'simple',
              responseFormat: data.responseFormat as ResponseFormatType,
              confidence: data.confidence || 0,
              reasoning: '',
            };
            console.log('Classification:', data);
          },
          onProvider: (data) => {
            console.log('Using provider:', data.provider);
          },
          onContent: (chunk) => {
            appendStreamContent(chunk);
          },
          onMetadata: (data) => {
            setStreamingStatus(null); // Clear status when response is ready

            // Build orchestration details from metadata
            const orchestrationDetails: OrchestrationDetails = {
              sourcesQueried: data.orchestrationDetails?.sourcesQueried || [],
              sourcesSucceeded: data.orchestrationDetails?.sourcesSucceeded || data.sourcesUsed || [],
              sourcesFailed: data.orchestrationDetails?.sourcesFailed || [],
              neo4jQuery: data.orchestrationDetails?.neo4jQuery,
              webSearchQuery: data.orchestrationDetails?.webSearchQuery,
              parallelExecution: data.orchestrationDetails?.parallelExecution ?? true,
              totalDurationMs: data.orchestrationDetails?.totalDurationMs || data.executionTimeMs || 0,
            };

            finishStreaming({
              messageId: finalMessageId,
              responseFormat: data.responseFormat as ResponseFormatType,
              confidence: {
                overall_score: data.confidence.overall_score,
                overall_level: data.confidence.overall_level as 'High' | 'Medium' | 'Low',
              },
              sources: data.sourcesUsed,
              executionTimeMs: data.executionTimeMs,
              queryClassification: classificationRef.current || undefined,
              orchestrationDetails,
            });
          },
          onFollowUp: (data) => {
            // Add follow-up questions to the message after it's finished streaming
            if (data.questions && data.questions.length > 0) {
              updateMessage(finalMessageId, {
                followUpQuestions: data.questions,
              });
            }
          },
          onDone: (data) => {
            console.log('Stream complete:', data);
          },
          onError: (errorMsg) => {
            setStreamingStatus(null);
            setError(errorMsg);
            finishStreaming();
            toast.error(`AI Assistant error: ${errorMsg}`);
          },
        }
      );
    } catch (err) {
      console.error('Streaming failed:', err);
      setStreamingStatus(null);
      finishStreaming();
      setError('Failed to get response');
      toast.error('Failed to get response. Trying fallback...');

      // Fallback to non-streaming
      try {
        await sendQuery(message, currentSessionId || undefined);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    }
  }, [currentUser?.id, currentSessionId, projectContext, addMessage, updateMessage, startStreaming, appendStreamContent, finishStreaming, setCurrentSession, setError, sendQuery]);

  // Handle send message (with mode toggle)
  const handleSendMessage = async (message: string) => {
    if (useStreamingMode) {
      await handleSendMessageWithStreaming(message);
    } else {
      // Original non-streaming implementation
      if (!currentUser?.id) {
        toast.error('Please log in to send messages');
        return;
      }

      try {
        await sendQuery(message, currentSessionId || undefined);
        setInputValue('');
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    }
  };

  // Handle sample question click
  const handleSampleQuestionClick = (question: string) => {
    setInputValue(question);
  };

  return (
    <div className="fixed inset-0 top-16 left-0 flex bg-neutral-50">
      {/* Chat History Sidebar */}
      <ChatHistorySidebar
        sessions={sessions}
        onSessionSelect={handleSessionSelect}
        onNewChat={handleNewChat}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b border-neutral-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Menu button to expand main navigation */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="h-8 w-8 p-0"
                title={sidebarCollapsed ? 'Show main navigation' : 'Hide main navigation'}
              >
                <Menu className="h-4 w-4" />
              </Button>

              <div>
                <h1 className="text-lg font-semibold text-neutral-900">IntelliKnow™ Assistant</h1>
                <p className="text-sm text-neutral-600 mt-0.5">
                  Ask questions about companies, enterprise systems, and business domains
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Project Context Indicator */}
              {projectContext && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full">
                  <Briefcase className="h-3 w-3 text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">
                    {projectContext.projectName || projectContext.companyName || 'Project Context'}
                  </span>
                  <button
                    onClick={() => {
                      // User can click to see full context - for now just show toast
                      toast.info(`Context: ${projectContext.companyName || ''} - ${projectContext.businessDomain || ''} (${projectContext.currentPhase || 'Active'})`);
                    }}
                    className="ml-1 hover:text-amber-900"
                    title="Click to see full context"
                  >
                    <span className="text-amber-500 text-xs">ⓘ</span>
                  </button>
                </div>
              )}

              {/* Streaming toggle */}
              <button
                onClick={() => setUseStreamingMode(!useStreamingMode)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  useStreamingMode
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-neutral-100 text-neutral-600'
                }`}
                title={useStreamingMode ? 'Streaming mode enabled' : 'Click to enable streaming'}
              >
                <Zap className={`h-3 w-3 ${useStreamingMode ? 'text-primary-600' : 'text-neutral-400'}`} />
                {useStreamingMode ? 'Streaming' : 'Standard'}
              </button>

              {healthData && (
                <div className="flex items-center gap-2 text-xs text-neutral-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Connected • {healthData.llm_provider}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-3 flex items-center gap-2 text-red-800">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Messages */}
        <MessageList
          messages={messages}
          isLoading={isLoading || isStreaming}
          isStreaming={isStreaming}
          streamingMessageId={streamingMessageId}
          streamingContent={streamingContent}
          streamingStatus={streamingStatus}
          onSampleQuestionClick={handleSampleQuestionClick}
        />

        {/* Input */}
        <MessageInput
          onSend={handleSendMessage}
          isLoading={isLoading}
          value={inputValue}
          onChange={setInputValue}
        />
      </div>
    </div>
  );
}
